import React, { useState, useEffect, createContext, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useCoachAffiliations } from '@/hooks/useCoachAffiliations';
import { LogOut, Menu, X, Users, BarChart3, Settings, User, ChevronDown, Globe, PanelLeftClose, PanelRightClose, Building2, MessageCircle, ClipboardList, Calendar, Home, ExternalLink } from 'lucide-react';
import { useFeatureFlags } from '@/utils/feature-flags';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AffiliationsPanel } from '@/components/coaching/coach/AffiliationsPanel';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import type { Language } from '@/i18n/types';

// ---------------------------------------------------------------------------
// CoachPortalContext — lives OUTSIDE CoachLayout so pages can consume it
// before their own <CoachLayout> renders. CoachPortalProvider must wrap the
// /coach-portal routes in App.tsx.
// ---------------------------------------------------------------------------

interface CoachPortalContextValue {
  selectedClubId: string | null;
  setSelectedClubId: (id: string | null) => void;
}

export const CoachPortalContext = createContext<CoachPortalContextValue>({
  selectedClubId: null,
  setSelectedClubId: () => {},
});

export function useCoachPortalContext() {
  return useContext(CoachPortalContext);
}

export function CoachPortalProvider({ children }: { children: React.ReactNode }) {
  const [selectedClubId, setSelectedClubId] = useState<string | null>(() => {
    const saved = localStorage.getItem('coachPortalSelectedClub');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    localStorage.setItem('coachPortalSelectedClub', JSON.stringify(selectedClubId));
  }, [selectedClubId]);

  return (
    <CoachPortalContext.Provider value={{ selectedClubId, setSelectedClubId }}>
      {children}
    </CoachPortalContext.Provider>
  );
}

interface CoachLayoutProps {
  children: React.ReactNode;
  headerAction?: React.ReactNode;
}

interface NavItem {
  label: string;
  path: string;
  icon: any;
}

export const CoachLayout: React.FC<CoachLayoutProps> = ({ children, headerAction }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const { t } = useAppTranslations();
  const { language, setLanguage } = useLanguage();
  const { activeAffiliations, pendingAffiliations, affiliations } = useCoachAffiliations();
  const { selectedClubId, setSelectedClubId } = useCoachPortalContext();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [affiliationsPanelOpen, setAffiliationsPanelOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('coachPortalSidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const { isEnabled } = useFeatureFlags(user?.id, selectedClubId || undefined);
  const consolidationOn = isEnabled('coach-sessions-consolidation');

  // Library is NOT a top-level nav item under consolidation. It lives inside
  // /coach-portal/sessions as sub-tabs (Exercises, Videos) because the library
  // exists to feed sessions — surfacing it as a peer nav item separated the
  // two things that belong together.
  const navItems: NavItem[] = [
    { label: t.coach.nav.schedule,   path: '/coach-portal/schedule',    icon: Calendar      },
    { label: t.coach.nav.myStudents, path: '/coach-portal/my-students', icon: Users         },
    { label: t.coach.nav.messages,   path: '/coach-portal/messages',    icon: MessageCircle },
    { label: t.coach.nav.sessions,   path: '/coach-portal/sessions',    icon: ClipboardList },
    { label: t.coach.nav.revenue,    path: '/coach-portal/pricing',     icon: BarChart3     },
    { label: t.coach.nav.profile,    path: '/coach-portal/profile',     icon: User          },
    { label: t.coach.nav.settings,   path: '/coach-portal/settings',    icon: Settings      },
  ];

  const languageOptions: { code: Language; label: string; flag: string }[] = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  ];

  const isEffectivelyCollapsed = sidebarCollapsed;

  useEffect(() => {
    localStorage.setItem('coachPortalSidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    setAvatarError(false);
  }, [profile?.avatar_url]);

  const handleLogout = async () => {
    await signOut();
    navigate('/auth');
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 to-slate-100/80">
      {/* Mobile sidebar overlay */}
      {sidebarOpen ? (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      {/* Sidebar */}
      <TooltipProvider>
        <div
          className={cn(
            "shadow-xl border-r border-slate-200/60 z-50",
            "fixed inset-y-0 left-0 transform md:relative md:translate-x-0 transition-all duration-300 ease-in-out",
            sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
            "flex flex-col h-full rounded-tr-3xl rounded-br-3xl",
            isEffectivelyCollapsed ? "w-16" : "w-64",
            "bg-white md:bg-white/80 md:backdrop-blur-sm"
          )}
        >
          {/* Collapse Toggle Button - Desktop Only */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden md:flex absolute -right-3 top-20 z-10 w-6 h-6 bg-white rounded-full border border-slate-200 shadow-lg items-center justify-center hover:bg-slate-50 transition-colors"
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? (
              <PanelRightClose className="h-4 w-4 text-slate-600" />
            ) : (
              <PanelLeftClose className="h-4 w-4 text-slate-600" />
            )}
          </button>

          {/* Coach Header */}
          <div
            className={cn(
              "flex-shrink-0 transition-all duration-300 safe-area-top-4",
              isEffectivelyCollapsed ? "px-4 pb-4" : "px-6 pb-6"
            )}
            style={{ borderBottom: '1px solid rgba(15,23,42,0.08)' }}
          >
            <div className={cn("transition-all duration-300", isEffectivelyCollapsed ? "" : "space-y-3")}>
              {isEffectivelyCollapsed ? (
                <div className="flex justify-center">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg overflow-hidden bg-white"
                  >
                    <img src="/strokon-icon.png" alt="Strokone" className="w-8 h-8 object-contain" />
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0 overflow-hidden bg-white"
                    >
                      <img src="/strokon-icon.png" alt="Strokone" className="w-8 h-8 object-contain" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="font-bold text-slate-900 truncate">Strokone</h2>
                      <p className="text-xs text-slate-500 truncate">{t.coach.nav.coachPortal}</p>
                    </div>
                  </div>

                  {/* Club Switcher */}
                  {affiliations.length > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full justify-between bg-white/80 border-slate-200 shadow-sm hover:bg-white transition-all duration-200"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <ClubAvatar
                              name={selectedClubId
                                ? affiliations.find(a => a.clubId === selectedClubId)?.club?.name || 'Club'
                                : 'All'}
                              logoUrl={selectedClubId
                                ? affiliations.find(a => a.clubId === selectedClubId)?.club?.logo_url ?? null
                                : null}
                              size={16}
                            />
                            <span className="text-xs truncate">
                              {selectedClubId
                                ? affiliations.find(a => a.clubId === selectedClubId)?.club?.name || t.coach.clubSwitcher.selectClub
                                : t.coach.clubSwitcher.allStudents}
                            </span>
                          </div>
                          <ChevronDown className="h-3 w-3 ml-1 flex-shrink-0" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-full">
                        <DropdownMenuItem onClick={() => setSelectedClubId(null)}>
                          <span className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            {t.coach.clubSwitcher.allStudents}
                          </span>
                        </DropdownMenuItem>
                        {affiliations.map((aff) => (
                          <DropdownMenuItem
                            key={aff.id}
                            onClick={() => setSelectedClubId(aff.clubId)}
                            className={cn(selectedClubId === aff.clubId && 'bg-blue-50 text-blue-700')}
                          >
                            <span className="flex items-center gap-2">
                              <ClubAvatar
                                name={aff.club?.name ?? ''}
                                logoUrl={aff.club?.logo_url ?? null}
                                size={16}
                              />
                              {aff.club?.name}
                              {aff.status === 'pending' && <span className="text-xs text-amber-600 ml-1">{t.coach.clubSwitcher.pending}</span>}
                            </span>
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setAffiliationsPanelOpen(true)}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          <span className="flex items-center gap-2">
                            <ExternalLink className="h-3.5 w-3.5" />
                            <span className="text-xs">{t.coach.clubSwitcher.manageAffiliations}</span>
                          </span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="p-4 flex-1 overflow-y-auto overflow-x-hidden">
            <ul className="space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                const buttonContent = (
                  <Button
                    style={isActive ? { background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' } : {}}
                    className={cn(
                      "w-full rounded-xl transition-all duration-200 h-12",
                      isEffectivelyCollapsed ? "justify-center px-2" : "justify-start px-4",
                      isActive
                        ? "text-white font-semibold shadow-lg hover:shadow-xl"
                        : "bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900 hover:shadow-md font-medium"
                    )}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                  >
                    <Icon className={cn("h-5 w-5", !isEffectivelyCollapsed && "mr-3")} />
                    {!isEffectivelyCollapsed && <span className="flex-1 text-left">{item.label}</span>}
                  </Button>
                );

                return (
                  <li key={item.path}>
                    {isEffectivelyCollapsed ? (
                      <Tooltip>
                        <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
                        <TooltipContent side="right" className="ml-2">
                          {item.label}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      buttonContent
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User Info & Logout */}
          <div
            className={cn(
              "flex-shrink-0 transition-all duration-300",
              isEffectivelyCollapsed ? "p-2" : "p-4"
            )}
            style={{ borderTop: '1px solid rgba(15,23,42,0.08)' }}
          >
            {isEffectivelyCollapsed ? (
              <div className="flex flex-col items-center space-y-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shadow-sm cursor-pointer overflow-hidden relative">
                      {profile?.avatar_url && !avatarError ? (
                        <img
                          src={profile.avatar_url}
                          alt={profile.display_name || user?.email || 'User'}
                          className="w-full h-full object-cover"
                          onError={() => setAvatarError(true)}
                        />
                      ) : null}
                      <div
                        className={cn(
                          "w-full h-full bg-gradient-to-br from-slate-400 to-slate-600 rounded-full flex items-center justify-center",
                          profile?.avatar_url && !avatarError ? "absolute inset-0 opacity-0" : "static"
                        )}
                      >
                        <span className="text-white text-sm font-bold">
                          {profile?.display_name?.charAt(0).toUpperCase() ||
                            user?.email?.charAt(0).toUpperCase() ||
                            'U'}
                        </span>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="ml-2">
                    <div>
                      <p className="font-semibold">
                        {profile?.display_name || user?.email?.split('@')[0] || 'User'}
                      </p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                    </div>
                  </TooltipContent>
                </Tooltip>

                {/* Language Selector - Collapsed */}
                <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-500 hover:text-slate-700 hover:bg-white/80 rounded-lg w-8 h-8 p-0"
                        >
                          <span className="text-base">
                            {languageOptions.find((l) => l.code === language)?.flag || '🌐'}
                          </span>
                        </Button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="ml-2">
                      {t.navigation.language ?? 'Language'}
                    </TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent align="end" side="right" className="ml-2">
                    {languageOptions.map((lang) => (
                      <DropdownMenuItem
                        key={lang.code}
                        onClick={() => setLanguage(lang.code)}
                        className={cn(
                          "flex items-center space-x-2 cursor-pointer",
                          language === lang.code && "bg-blue-50 text-blue-700"
                        )}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.label}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleLogout}
                      className="text-slate-500 hover:text-slate-700 hover:bg-white/80 rounded-lg w-8 h-8 p-0"
                    >
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="ml-2">
                    {t.navigation.logout ?? 'Logout'}
                  </TooltipContent>
                </Tooltip>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Language Selector Row */}
                <div className="flex items-center justify-between px-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-500 hover:text-slate-700 hover:bg-white/80 rounded-lg px-2 h-8"
                      >
                        <Globe className="h-4 w-4 mr-1.5" />
                        <span className="text-xs">
                          {languageOptions.find((l) => l.code === language)?.label || 'English'}
                        </span>
                        <ChevronDown className="h-3 w-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="top" className="w-36">
                      {languageOptions.map((lang) => (
                        <DropdownMenuItem
                          key={lang.code}
                          onClick={() => setLanguage(lang.code)}
                          className={cn(
                            "flex items-center space-x-2 cursor-pointer",
                            language === lang.code && "bg-blue-50 text-blue-700"
                          )}
                        >
                          <span>{lang.flag}</span>
                          <span>{lang.label}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* User Info Row */}
                <div className="flex items-center justify-between bg-white/50 backdrop-blur-sm rounded-xl p-3">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center shadow-sm overflow-hidden relative">
                      {profile?.avatar_url && !avatarError ? (
                        <img
                          src={profile.avatar_url}
                          alt={profile.display_name || user?.email || 'User'}
                          className="w-full h-full object-cover"
                          onError={() => setAvatarError(true)}
                        />
                      ) : null}
                      <div
                        className={cn(
                          "w-full h-full bg-gradient-to-br from-slate-400 to-slate-600 rounded-full flex items-center justify-center",
                          profile?.avatar_url && !avatarError
                            ? "absolute inset-0 opacity-0"
                            : "static"
                        )}
                      >
                        <span className="text-white text-sm font-bold">
                          {profile?.display_name?.charAt(0).toUpperCase() ||
                            user?.email?.charAt(0).toUpperCase() ||
                            'U'}
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {profile?.display_name || user?.email?.split('@')[0] || 'User'}
                      </p>
                      <p className="text-xs text-slate-600 truncate">{user?.email}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="text-slate-500 hover:text-slate-700 hover:bg-white/80 rounded-lg flex-shrink-0"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </TooltipProvider>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden md:ml-0">
        {/* Top Header */}
        <header
          className="px-3 sm:px-6 pb-3 sm:pb-5 flex-shrink-0 safe-area-top-4"
          style={{
            background: 'rgba(255,255,255,0.82)',
            backdropFilter: 'blur(20px) saturate(180%)',
            borderBottom: '1px solid rgba(15,23,42,0.08)',
            boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 8px rgba(15,23,42,0.06)',
          }}
        >
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden flex-shrink-0 p-1.5"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>

              <div
                className="p-2 sm:p-3 rounded-xl text-white shadow-lg flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' }}
              >
                {(() => {
                  const currentItem = navItems.find((item) => item.path === location.pathname);
                  const Icon = currentItem?.icon || Home;
                  return <Icon className="h-5 w-5 sm:h-6 sm:w-6" />;
                })()}
              </div>

              <div className="min-w-0 flex-1">
                <h1 className="text-[18px] sm:text-[22px] font-bold tracking-tight text-slate-900 truncate">
                  {(() => {
                    const currentPath = location.pathname;
                    if (currentPath === '/coach-portal/schedule') return t.coach.header.scheduleTitle;
                    if (currentPath === '/coach-portal/my-students') return t.coach.header.studentsTitle;
                    if (currentPath === '/coach-portal/messages') return t.coach.header.messagesTitle;
                    if (currentPath === '/coach-portal/sessions') return t.coach.header.sessionsTitle;
                    if (currentPath === '/coach-portal/pricing') return t.coach.header.revenueTitle;
                    if (currentPath === '/coach-portal/profile') return t.coach.header.profileTitle;
                    if (currentPath === '/coach-portal/settings') return t.coach.header.settingsTitle;
                    if (currentPath.startsWith('/coach-portal/students/')) return t.coach.header.studentProfileTitle;
                    return t.coach.nav.coachPortal;
                  })()}
                </h1>
                <p className="text-xs sm:text-sm text-slate-500 font-medium truncate">
                  {(() => {
                    const currentPath = location.pathname;
                    if (currentPath === '/coach-portal/schedule') return t.coach.header.scheduleSubtitle;
                    if (currentPath === '/coach-portal/my-students') return t.coach.header.studentsSubtitle;
                    if (currentPath === '/coach-portal/messages') return t.coach.header.messagesSubtitle;
                    if (currentPath === '/coach-portal/sessions') return t.coach.header.sessionsSubtitle;
                    if (currentPath === '/coach-portal/pricing') return t.coach.header.revenueSubtitle;
                    if (currentPath === '/coach-portal/profile') return t.coach.header.profileSubtitle;
                    if (currentPath === '/coach-portal/settings') return t.coach.header.settingsSubtitle;
                    if (currentPath.startsWith('/coach-portal/students/')) return t.coach.header.studentProfileSubtitle;
                    return '';
                  })()}
                </p>
              </div>
            </div>
            {headerAction && <div className="flex-shrink-0">{headerAction}</div>}
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-auto bg-gradient-to-br from-slate-50 to-slate-100/80">
          <div className="p-3 sm:p-6 min-h-full">{children}</div>
        </main>
      </div>

      <AffiliationsPanel
        open={affiliationsPanelOpen}
        onClose={() => setAffiliationsPanelOpen(false)}
      />
    </div>
  );
};

// ── ClubAvatar — shared mini avatar for club logos in dropdowns and lists ────
// logo if available, else 2-letter initials on deterministic gradient.

const CLUB_GRADIENTS = [
  'linear-gradient(135deg,#1B4332,#2D5A3D)',
  'linear-gradient(135deg,#2A4494,#1E3270)',
  'linear-gradient(135deg,#F59E0B,#D97706)',
  'linear-gradient(135deg,#7C3AED,#5B21B6)',
  'linear-gradient(135deg,#DC2626,#B91C1C)',
  'linear-gradient(135deg,#0891B2,#0E7490)',
  'linear-gradient(135deg,#059669,#047857)',
  'linear-gradient(135deg,#DB2777,#BE185D)',
];

const clubGradient = (name: string) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return CLUB_GRADIENTS[Math.abs(h) % CLUB_GRADIENTS.length];
};

const clubInitials = (name: string) =>
  name.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);

interface ClubAvatarProps { name: string; logoUrl: string | null; size?: number }

export const ClubAvatar: React.FC<ClubAvatarProps> = ({ name, logoUrl, size = 20 }) => {
  const r = Math.round(size * 0.28);
  const fontSize = Math.max(8, Math.round(size * 0.38));
  if (logoUrl) {
    return (
      <span
        className="flex-shrink-0 inline-flex items-center justify-center overflow-hidden bg-white"
        style={{ width: size, height: size, borderRadius: r, border: '1px solid rgba(15,23,42,0.10)' }}
      >
        <img src={logoUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 1 }} />
      </span>
    );
  }
  return (
    <span
      className="flex-shrink-0 inline-flex items-center justify-center"
      style={{ width: size, height: size, borderRadius: r, background: clubGradient(name || '?') }}
    >
      <span style={{ fontSize, fontWeight: 800, color: '#fff', lineHeight: 1 }}>
        {clubInitials(name || '?')}
      </span>
    </span>
  );
};
